import { type Player } from "@/lib/types/roster"
import { usePositionEligibility } from "@/lib/utils/position-eligibility"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { IL_ELIGIBLE_STATUS_CODES } from "./teams/PositionButton"
import { PlayerLink } from "@/components/players/player-link"
import { cn } from "@/lib/utils"
import { NewspaperIcon } from "lucide-react"
import { formatDateForDisplay } from "@/lib/utils/date"

interface PlayerInfoProps {
    player: Player
    'aria-label'?: string
    teamLabel?: string
    positionsLabel?: string
    gameStatus?: {
        position: {
            isStarting: boolean
            isBench: boolean
            positionCode: string
        }
    }
    onPrimaryBackground?: boolean
    // Optional navigation props for player modal navigation
    navigationData?: {
        playerIds: number[]
        context?: string
    }
    // Optional sport info for minor league players
    sportInfo?: {
        name: string
        isMinorLeague: boolean
        sportId: number
    }
    // Optional LOD team name
    lodTeam?: string
}

export function PlayerInfo({ player, 'aria-label': ariaLabel, teamLabel, positionsLabel, gameStatus, onPrimaryBackground, navigationData, sportInfo, lodTeam }: PlayerInfoProps) {
    // If this is an empty player (id starts with 'empty-'), return an empty div
    if (player.id.toString().startsWith('empty-')) {
        return <div className="flex gap-2 sm:gap-3 items-start min-w-fit" />
    }

    const eligiblePositions = usePositionEligibility(player.positionCounts, Number(player.id), player.positionOverrides)
    const positionCodes = eligiblePositions.join(', ')
    const displayName = {
        full: `${player.firstName || ''} ${player.lastName || ''}`.trim(),
        compact: `${player.firstName ? player.firstName.charAt(0) + '.' : ''} ${player.lastName || ''}`.trim()
    }

    const latestNews = player.newsItems?.[0]

    return (
        <div className="flex gap-2 sm:gap-3 items-start min-w-fit" aria-label={ariaLabel}>
            {player.mlbPlayerId && (
                <img
                    src={`https://securea.mlb.com/mlb/images/players/head_shot/${player.mlbPlayerId}.jpg`}
                    alt={`${player.firstName} ${player.lastName} headshot`}
                    className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded-full flex-shrink-0 my-[3px]"
                    onError={(e) => {
                        // Remove the img element if the image fails to load
                        e.currentTarget.style.display = 'none';
                    }}
                />
            )}
            <div className="flex flex-col min-w-0 gap-0.5">
                <div className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap">
                    <PlayerLink
                        playerId={Number(player.id)}
                        playerName={displayName.full}
                        className={cn(
                            "font-medium p-0 h-auto",
                            "sm:block hidden",
                            onPrimaryBackground && "text-primary-foreground"
                        )}
                        navigationData={navigationData}
                    />
                    <PlayerLink
                        playerId={Number(player.id)}
                        playerName={displayName.compact}
                        className={cn(
                            "font-medium p-0 h-auto",
                            "sm:hidden block",
                            onPrimaryBackground && "text-primary-foreground"
                        )}
                        navigationData={navigationData}
                    />
                    {latestNews && (
                        <>
                            {/* Desktop View - Tooltip */}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild className="hidden sm:block">
                                        <NewspaperIcon className={cn(
                                            "h-4 w-4 transition-colors cursor-pointer flex-shrink-0",
                                            onPrimaryBackground
                                                ? "text-primary-foreground/70 hover:text-primary-foreground"
                                                : "text-muted-foreground hover:text-foreground"
                                        )} />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[500px]">
                                        <NewsContent news={latestNews} />
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            {/* Mobile View - Popover */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <NewspaperIcon className={cn(
                                        "h-4 w-4 transition-colors cursor-pointer sm:hidden",
                                        onPrimaryBackground
                                            ? "text-primary-foreground/70 hover:text-primary-foreground"
                                            : "text-muted-foreground hover:text-foreground"
                                    )} />
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[calc(100vw-2rem)] max-w-[500px] sm:hidden rounded-md border bg-primary text-primary-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                                    sideOffset={5}
                                >
                                    <NewsContent news={latestNews} />
                                </PopoverContent>
                            </Popover>
                        </>
                    )}
                </div>
                <div className={cn(
                    "text-xs sm:text-sm flex flex-wrap gap-x-1 items-center",
                    onPrimaryBackground ? "text-primary-foreground/70" : "text-muted-foreground"
                )}>
                    <span
                        aria-label={teamLabel || `Team: ${player.mlbTeam?.abbreviation || 'No team'}`}
                        data-testid="player-team"
                    >
                        {player.mlbTeam?.abbreviation || ''}
                    </span>
                    {lodTeam && (
                        <>
                            <span>•</span>
                            <span data-testid="lod-team">
                                {lodTeam}
                            </span>
                        </>
                    )}
                    {positionCodes && (
                        <span
                            aria-label={`Positions: ${positionCodes}`}
                            data-testid="player-positions"
                        >
                            {`(${positionCodes})`}
                        </span>
                    )}
                    {sportInfo?.isMinorLeague && sportInfo.name && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200`}>
                            {sportInfo.name}
                        </span>
                    )}
                    {gameStatus && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <span className={cn(
                                        "px-1 sm:px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0",
                                        gameStatus.position.isStarting ? "bg-green-100 text-green-800" :
                                            gameStatus.position.isBench ? "bg-yellow-100 text-yellow-800" : ""
                                    )}>
                                        {gameStatus.position.isStarting ?
                                            (gameStatus.position.positionCode === 'SP' ? "PP" : "S") :
                                            gameStatus.position.isBench ? "!" : ""}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent className={cn(
                                    gameStatus.position.isStarting ? "bg-green-100 text-green-800 border-green-200" :
                                        gameStatus.position.isBench ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
                                            "bg-white"
                                )}>
                                    <p>
                                        {gameStatus.position.isStarting
                                            ? (gameStatus.position.positionCode === 'SP'
                                                ? "Probable Pitcher"
                                                : `Starting at ${gameStatus.position.positionCode}`)
                                            : gameStatus.position.isBench
                                                ? "On Bench"
                                                : "Not Playing"}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {player.playerStatus && IL_ELIGIBLE_STATUS_CODES.includes(player.playerStatus.code) && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <span className={cn(
                                        "px-1 sm:px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0",
                                        player.ilGameEligible === false
                                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                            : "bg-red-100 text-red-800"
                                    )}>
                                        IL
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent className={player.ilGameEligible === false
                                    ? "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-800"
                                    : "bg-red-100 text-red-800 border-red-200"
                                }>
                                    <p>{player.playerStatus.description}</p>
                                    {player.ilPlacedDate && (
                                        <p className="text-xs mt-0.5">
                                            Since {formatDateForDisplay(new Date(player.ilPlacedDate))}
                                        </p>
                                    )}
                                    {player.ilGameEligible === false && (
                                        <p className="text-xs mt-0.5">Not LOD IL eligible — MLB IL placement not confirmed</p>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {player.exemptFromPlayerCount && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <span className="px-1 sm:px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded flex-shrink-0">
                                        EX
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800">
                                    <p>Exempt from player count</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </div>
        </div>
    )
}

function NewsContent({ news }: { news: { title: string; date: string; content: string } }) {
    return (
        <div className="space-y-2">
            <div className="flex flex-col gap-1">
                <p className="font-medium break-words">{news.title}</p>
                <p className="text-xs">{formatDateForDisplay(new Date(news.date), 'short')}</p>
            </div>
            <p className="text-sm border-t pt-2">{news.content}</p>
        </div>
    )
}